import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import cx from 'classnames';
import VisibilitySensor from 'react-visibility-sensor';

import { INPUT_IMPRESSION, INPUT_CLICK, INPUT_CHANGE, INPUT_FOCUS, PROMO_CODE_APPLY } from 'lib/analytics/events';

import { Location } from 'models/locations';

import CurrencySymbol from 'components/common/currency-symbol';

import trackEventCreator from 'action-creators/analytics/track-event';
import getPreviewCreator from 'action-creators/checkout/get-preview';

const mapStateToProps = (state) => {
  const { account, checkout, search } = state;

  return {
    couponAmount: checkout.couponAmount,
    couponCode: checkout.couponCode,
    error: checkout.error,
    isReseller: account.user.isReseller,
    location: search.selectedLocation,
  };
};

const mapDispatchToProps = dispatch => (
  bindActionCreators({
    getPreview: getPreviewCreator,
    trackEvent: trackEventCreator,
  }, dispatch)
);

const propTypes = {
  couponAmount: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
  couponCode: PropTypes.string,
  error: PropTypes.instanceOf(Map),
  getPreview: PropTypes.func.isRequired,
  isReseller: PropTypes.bool,
  location: PropTypes.instanceOf(Location),
  trackEvent: PropTypes.func.isRequired,
};

const defaultProps = {
  couponAmount: null,
  couponCode: null,
  error: new Map(),
  isReseller: false,
  location: new Location(),
};

class CouponCode extends Component {
  static getDerivedStateFromProps(nextProps, prevState) {
    return {
      ...prevState,
      couponCode: nextProps.couponCode,
      couponCodeExpanded: prevState.couponCodeExpanded || (!prevState.couponCode && nextProps.couponCode),
    };
  }

  constructor(props) {
    super(props);

    this.handleApplyCoupon = this.handleApplyCoupon.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleEnterPress = this.handleEnterPress.bind(this);
    this.handleInputClick = this.handleInputClick.bind(this);
    this.handleInputFocus = this.handleInputFocus.bind(this);
    this.handleInputVisibilityChange = this.handleInputVisibilityChange.bind(this);
    this.handleRemoveCoupon = this.handleRemoveCoupon.bind(this);
    this.handleShowCouponCode = this.handleShowCouponCode.bind(this);
    this.trackInputEvent = this.trackInputEvent.bind(this);

    const { couponCode } = props;
    this.state = {
      couponCode: (couponCode !== '') ? couponCode : null,
      couponCodeExpanded: (couponCode !== null) && (couponCode !== ''),
    };
  }

  handleApplyCoupon(e) {
    e.preventDefault();
    const { getPreview, trackEvent } = this.props;

    const couponCode = this._couponCode.value;

    getPreview({ couponCode, autoApplyCoupon: false });
    trackEvent({
      ...PROMO_CODE_APPLY,
      properties: {
        PromoCode: couponCode,
      },
    });
  }

  handleChange() {
    this.setState({
      couponFieldHasContents: this._couponCode.value.length > 0,
    });
    this.trackInputEvent(INPUT_CHANGE);
  }

  handleEnterPress(e) {
    if (e.charCode === 13) {
      this.handleApplyCoupon(e);
    }
  }

  handleInputClick() {
    this.trackInputEvent(INPUT_CLICK);
  }

  handleInputFocus() {
    this.trackInputEvent(INPUT_FOCUS);
  }

  handleInputVisibilityChange(visible) {
    if (visible) {
      this.trackInputEvent(INPUT_IMPRESSION);
    }
  }

  handleRemoveCoupon() {
    this.props.getPreview({ couponCode: null, autoApplyCoupon: false });
  }

  handleShowCouponCode(e) {
    e.preventDefault();

    this.setState({
      couponCodeExpanded: true,
    });
  }

  trackInputEvent(event) {
    const { trackEvent } = this.props;

    trackEvent({
      ...event,
      name: 'PromoCode',
    });
  }

  renderApplyButton() {
    if (this.state && this.state.couponFieldHasContents) {
      return (
        <div className="col-xs-6 margin-top-xs-15">
          <a className="apply-coupon" onClick={this.handleApplyCoupon}>
            APPLY
          </a>
        </div>
      );
    }
    return null;
  }

  renderCollapsed() {
    return (
      <div onClick={this.handleShowCouponCode} className="col-xs-12 clickable text-color-blue text-weight-bold text-size-sm-16 text-size-xs-14">
        + ADD PROMO CODE
      </div>
    );
  }

  renderCouponError() {
    const { error } = this.props;

    const errorCode = error.get('code') || '';

    if (errorCode.match(/coupon/)) {
      const errorMessage = error.get('message') || 'Invalid coupon code';

      return (
        <div style={{ color: 'red' }}>
          {errorMessage}
        </div>);
    }

    return null;
  }

  renderCouponField() {
    const classes = cx('form-control', {
      uppercase: this.state.couponFieldHasContents,
    });

    return (
      <div className="row margin-0">
        <div className="col-xs-12">
          <label htmlFor="code">Promo Code</label>
        </div>
        <div className="col-xs-6">
          <VisibilitySensor onChange={this.handleInputVisibilityChange} partialVisibility>
            <input
              ref={(c) => { this._couponCode = c; }}
              name="code"
              placeholder="Have a promo code?"
              id="code"
              className={classes}
              type="text"
              onChange={this.handleChange}
              onKeyPress={this.handleEnterPress}
              onClick={this.handleInputClick}
              onFocus={this.handleInputFocus}
            />

          </VisibilitySensor>
          {this.renderCouponError()}
        </div>
        {this.renderApplyButton()}
      </div>
    );
  }

  renderCouponInfo() {
    const { couponAmount, couponCode, location } = this.props;

    return (
      <div className="col-xs-12 text-size-sm-16 text-weight-book text-color-dark-slate">
        <div className="hidden-xs col-sm-8 padding-0">
          Promo Code
          <strong className="uppercase padding-horizontal-sm-10">
            {couponCode}
          </strong>
          {
            couponAmount > 0 && (
              <span className="uppercase text-color-blue">
                <a className="remove-coupon" onClick={this.handleRemoveCoupon}>
                  Remove Code
                </a>
              </span>
            )
          }
        </div>
        <div className="visible-xs col-xs-8 padding-0" >
          <div>
            Promo Code
          </div>
          <div>
            <strong className="uppercase">
              {couponCode}
            </strong>
            {
              couponAmount > 0 && (
                <span className="uppercase text-color-blue padding-left-xs-10">
                  <a className="remove-coupon" onClick={this.handleRemoveCoupon}>
                    Remove Code
                  </a>
                </span>
              )
            }
          </div>
        </div>
        <div className="col-xs-4 text-align-right padding-0">
          <span>
            -<CurrencySymbol location={location} />
            {parseFloat(couponAmount).toFixed(2)}
          </span>
        </div>
      </div>
    );
  }

  renderExpanded() {
    return (this.props.couponCode ? this.renderCouponInfo() : this.renderCouponField());
  }

  render() {
    const { isReseller } = this.props;

    if (isReseller) return null;

    return (
      <div className="new-coupon row">
        {this.state.couponCodeExpanded ? this.renderExpanded() : this.renderCollapsed()}
      </div>
    );
  }
}

CouponCode.propTypes = propTypes;
CouponCode.defaultProps = defaultProps;

export { CouponCode };

export default connect(mapStateToProps, mapDispatchToProps)(CouponCode);
